<template>
  <a-upload
    v-model:file-list="fileList"
    :customRequest="customRequest"
    :list-type="listType"
    :max-count="maxCount"
    @change="handleChange"
    @preview="handlePreview"
    :accept="acceptList && acceptList.join(',')"
  >
    <a-button v-if="listType === 'picture'">
      <plus-outlined />
      上传
    </a-button>
    <template v-else>
      <plus-outlined />
      <div class="ant-upload-text">上传</div>
    </template>
  </a-upload>
  <a-modal
    :open="previewVisible"
    :title="previewTitle"
    :footer="null"
    @cancel="handleCancel"
    :width="800"
  >
    <img
      alt="example"
      style="width: 100%"
      v-if="previewImage"
      :src="previewImage"
    />
    <embed
      v-if="previewPdf"
      :src="previewPdf"
      type="application/pdf"
      width="100%"
      height="500px"
    />
  </a-modal>
</template>

<script lang="ts">
import { PlusOutlined } from '@ant-design/icons-vue';
import { defineComponent, onMounted, ref, watch } from 'vue';
import { get_upload_files_url, upload_file } from 'liyu-pc-base/api/nsbcs-model';
import { message } from 'ant-design-vue';
import { cloneDeep, isArray, isEqual } from 'lodash';
import { useUserStore } from 'liyu-pc-base/init-plugin';

function getBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

interface FileItem {
  uid: string;
  fid?: any;
  name?: string;
  status?: string;
  response?: Response;
  percent?: number;
  url: string;
  preview?: string;
  originFileObj?: any;
  type?: string;
}

export default defineComponent({
  name: 'Upload',
  components: { PlusOutlined },
  props: {
    group: {
      type: String,
      required: true,
      default: () => 'sys',
    },
    access_type: {
      type: String,
      required: true,
      default: () => 'public',
    },
    listType: {
      type: String,
      required: true,
      default: () => 'picture',
    },
    maxCount: {
      type: Number,
      default: () => 10,
    },
    value: {
      type: Array as () => any[],
      required: true,
      default: () => [],
    },
    acceptList: {
      type: Array as () => string[],
      default: () => [],
    },
  },
  emits: ['update:value'],
  setup(props, { emit }) {
    const userStore = useUserStore();
    const current_org = userStore.currentOrg; // 当前组织org
    const previewVisible = ref<boolean>(false);
    const previewImage = ref<string | undefined>('');
    const previewPdf = ref<string | undefined>('');
    const previewTitle = ref<string | undefined>('');

    const handleCancel = () => {
      previewVisible.value = false;
      previewTitle.value = '';
    };
    const handlePreview = async (file: FileItem) => {
      if (!file.url && !file.preview) {
        file.preview = (await getBase64(file.originFileObj)) as string;
      }
      const value = file.url || file.preview;
      const ext = extname(file.url);
      if (
        file?.type === 'application/pdf' ||
        (typeof ext === 'string' && ext.toLowerCase() === 'pdf')
      ) {
        previewPdf.value = value;
        previewImage.value = null;
      } else {
        previewImage.value = value;
        previewPdf.value = null;
      }
      previewVisible.value = true;
      previewTitle.value = file.name || file.url.substring(file.url.lastIndexOf('/') + 1);
    };
    const fileList = ref<FileItem[]>([]);
    const extname = (filename: any) => {
      if (!filename || typeof filename != 'string') {
        return false;
      }
      const a = filename.split('').reverse().join('');
      const b = a.substring(0, a.search(/\./)).split('').reverse().join('');
      return b;
    };
    const innerValue = ref<{ id: any; url: string; name: string }[]>([]);
    const customRequest = async (fileobj: any) => {
      const type = extname(fileobj.file.name);
      const res = await get_upload_files_url({
        access_type: props.access_type,
        filetype: type,
        filename: fileobj.file.name,
        group: props.group,
        author: 0,
      });
      const formData = new FormData();
      formData.append('file', fileobj.file);
      formData.append('file_id', res.params.file_id);
      formData.append('key', res.params.key);
      await upload_file(formData, {
        onUploadProgress: (progressEvent: any) => {
          const complete = ((progressEvent.loaded / progressEvent.total) * 100) | 0;
          fileobj.onProgress({ percent: complete });
        },
      });
      fileobj.file.fid = res.params.file_id.toString();
      let url = res.params.url;
      if (url.includes('?')) {
        url = `${url}&org_id=${current_org.id}`;
      } else {
        url = `${url}?org_id=${current_org.id}`;
      }
      fileobj.file.url = url;
      fileobj.onSuccess();
      message.success('上传成功');
    };
    const handleChange = ({
      file,
      fileList: newFileList,
    }: {
      file: FileItem;
      fileList: FileItem[];
    }) => {
      if (file.status === 'uploading') {
        return;
      }
      const newValue = newFileList
        .filter(f => f.status === 'done' || f.status === 'removed')
        .map(f => ({
          id: f.fid,
          url: f.url,
          name: f.name,
        }));

      if (!isEqual(newValue, innerValue.value)) {
        innerValue.value = newValue;
        emit('update:value', innerValue.value);
      }
    };
    const syncValue = (value: any[] | undefined) => {
      if (isArray(value)) {
        innerValue.value = cloneDeep(value);
        fileList.value = innerValue.value.map(file => {
          const id = file.id ? file.id.toString() : new Date().getTime().toString();
          const url = file.url || '';
          const name = file.name || url.substring(url.lastIndexOf('/') + 1);
          return {
            uid: id,
            fid: file.id,
            name: name,
            status: 'done',
            url: url,
          };
        });
      } else {
        fileList.value = [];
        innerValue.value = [];
      }
    };

    onMounted(() => {
      syncValue(props.value);
    });
    watch(
      () => props.value,
      newValue => {
        if (!isEqual(newValue, innerValue.value)) {
          syncValue(newValue);
        }
      },
      { deep: true },
    );
    return {
      props,
      handleChange,
      customRequest,
      previewVisible,
      previewTitle,
      previewImage,
      previewPdf,
      handleCancel,
      handlePreview,
      fileList,
    };
  },
});
</script>
